// SPDX-License-Identifier: LicenseRef-Pachamama-Proprietary
// Copyright 2026 Pachamama Studios B.V. All rights reserved.

/**
 * AYNI — what a pin is called, in one place for every environment (the
 * Data tab, the graph, both globes, the headset's Data page, the CSV, Lab
 * space). Owner, 2026-10-09: a pin can be renamed; the name given wins,
 * else its own ("Highest", "Lowest", "Pin n").
 */

import { t } from '../../i18n'
import type { Pin } from './pins'

type Named = Pick<Pin, 'tag'> & { readonly name?: string | null }

/** The pin's name as shown: the one given, else "Highest", "Lowest" or "Pin n" (n from 1). */
export function pinName(pin: Named, index: number): string {
  if (pin.name) return pin.name
  if (pin.tag === 'max') return t('tools.data.pins.highest')
  if (pin.tag === 'min') return t('tools.data.pins.lowest')
  return t('tools.data.pins.name', { n: index + 1 })
}

/** The same in English, for the figure and the methods paragraph (like the citation); a given name as typed. */
export function pinEnglishName(pin: Named, index: number): string {
  if (pin.name) return pin.name
  if (pin.tag === 'max') return 'Highest value' // i18n-exempt: figure and methods text, English like the citation
  if (pin.tag === 'min') return 'Lowest value' // i18n-exempt: figure and methods text, English like the citation
  return `Pin ${index + 1}` // i18n-exempt: figure and methods text, English like the citation
}

/**
 * A pin's label on a globe (the browser's and the headset's): its value,
 * with the name in front once the viewer has given it one. A pin's own
 * name stays off the globe, as before: its colour ties it to the list.
 */
export function pinMarkLabel(name: string | null, value: string): string {
  if (!name) return value
  return value ? `${name} · ${value}` : name
}
